import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { isEmpty } from 'validate.js';
import { useDispatch } from 'react-redux';

import { pushParams } from 'dpl/actions/query_params';
import Icon from 'dpl/common/components/Icon';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { PORTAL_MOUNT_EL } from 'dpl/common/tooltip/components/FloatingTooltipWrapper';
import SmartLink from 'dpl/components/SmartLink';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { SIGNUP_FORM_TYPES } from 'dpl/shared/constants/shared';
import {
  SIGNUP_REF_ACTION,
  SIGNUP_REF_ACTION_TYPES
} from 'dpl/auth/utils/constants';
import { isMobileUA } from 'dpl/shared/utils';

import useUserFollowsData from '../hooks/useUserFollowsData';
import useBuyerFavoritesSummaries from '../hooks/useBuyerFavoritesSummaries';
import {
  FOLLOWABLE_STATUSES,
  FOLLOWABLE_STATUS_MESSAGE,
  FOLLOWABLE_TYPES
} from '../utils/constants';
import FavoriteEngagementMobileMenu from './FavoriteEngagementMobileMenu';
import UserFollowSuccessPopover from './UserFollowSuccessPopover';

const DEFAULT_ICON_SIZE = '24px';

const DEFAULT_ICON_PROPS = {
  CHECKED: {
    name: 'fetch-favorite-fill',
    className: 'rubber-dark-mid',
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE
  },
  UNCHECKED: {
    name: 'fetch-favorite-fill',
    className: 'default-color o-50',
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE
  }
} as const;

const IS_MOBILE_UA = isMobileUA();

type FollowableType = (typeof FOLLOWABLE_TYPES)[keyof typeof FOLLOWABLE_TYPES];

interface FavoriteButtonAnalytics {
  breeder_profile_id?: number;
  breeder_name?: string;
  breed_id?: number;
  page_number?: number;
  position?: number;
  puppies_available?: number;
  transportation?: string[];
  view?: string;
  view_feature_location?: string;
  [key: string]: any;
}

interface IconProps {
  name?: string;
  label?: string | null;
  className?: string;
  width?: string;
  height?: string;
}

interface FavoriteButtonProps {
  analytics?: FavoriteButtonAnalytics;
  breederFirstName?: string;
  breederProfilePhotoUrl?: string;
  puppyName?: string;
  puppyPhotoUrl?: string;
  className?: string | null;
  followableTypeId: number;
  isFavorite?: boolean;
  followableType: FollowableType;
  targetEl?: HTMLElement | null;
  portalStyle?: React.CSSProperties | null;
  onChange?: (args: { status: string; followableTypeId: number }) => void;
  checkedIconProps?: IconProps;
  uncheckedIconProps?: IconProps;
}

export default function FavoriteButton({
  className = null,
  followableTypeId,
  isFavorite: initialIsFavorite = false,
  followableType,
  analytics = {},
  breederFirstName,
  breederProfilePhotoUrl,
  puppyName,
  puppyPhotoUrl,
  targetEl = PORTAL_MOUNT_EL as HTMLElement | null,
  portalStyle = null,
  onChange,
  checkedIconProps = {
    name: 'fetch-favorite-fill',
    label: null,
    className: 'rubber-dark-mid',
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE
  },
  uncheckedIconProps = {
    name: 'fetch-favorite-fill',
    label: null,
    className: 'default-color o-50',
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE
  }
}: FavoriteButtonProps) {
  const [showSuccessPopover, setShowSuccessPopover] = useState(false);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [popoverStatus, setPopoverStatus] = useState<string | null>(null);
  const dispatch = useDispatch();

  const [isFavorite, setIsFavorite] = useState<boolean>(initialIsFavorite);

  const iconProps = isFavorite
    ? { ...DEFAULT_ICON_PROPS.CHECKED, ...checkedIconProps }
    : { ...DEFAULT_ICON_PROPS.UNCHECKED, ...uncheckedIconProps };

  const {
    createUserFollow,
    removeUserFollow,
    userFollowsData,
    hasUserFollow: userFollow,
    isLoading,
    isUpdating
  } = useUserFollowsData(followableTypeId, {
    followable_type: followableType
  });

  const currentUser = useCurrentUser();
  const { isLoggedIn, value: currentUserData } = currentUser;

  const { summariesData, refetchAndCheckHasTwoFavorites } =
    useBuyerFavoritesSummaries(currentUserData?.data?.buyer_profile_id);

  function showPopover(status: string) {
    setPopoverStatus(status);
    setShowSuccessPopover(true);
  }

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (showSuccessPopover) {
      timeout = setTimeout(() => setShowSuccessPopover(false), 3000);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [showSuccessPopover]);

  useEffect(() => {
    if (!isLoading) setIsFavorite(!isEmpty(userFollow));
  }, [isLoading, userFollow]);

  async function updateFavorite(buyerProfileID?: number) {
    if (!isFavorite) {
      const eventName =
        followableType === FOLLOWABLE_TYPES.DOG
          ? ANALYTICS_EVENTS.PUPPY_ADDED_TO_FAVORITES
          : ANALYTICS_EVENTS.BREEDER_ADDED_TO_FAVORITES;

      sendRudderstackEvent(eventName, {
        buyer_profile_id:
          currentUserData.data?.buyer_profile_id || buyerProfileID,
        view_feature: ANALYTICS_VIEW_FEATURES.FAVORITE_HEART_ICON,
        user_journey: ANALYTICS_USER_JOURNEYS.BUYER_BROWSING,
        ...(followableType === FOLLOWABLE_TYPES.DOG && {
          dog_id: followableTypeId
        }),
        ...analytics
      });

      try {
        await createUserFollow({
          body: {
            followable_type: followableType,
            followable_id: followableTypeId
          }
        });
      } finally {
        await userFollowsData.refetch();
        const isSecondFavorite = await refetchAndCheckHasTwoFavorites();
        showPopover(FOLLOWABLE_STATUS_MESSAGE.ADDED_TO_FAVORITES);
        onChange?.({ status: FOLLOWABLE_STATUSES.ADDED, followableTypeId });

        if (IS_MOBILE_UA && isSecondFavorite) {
          setTimeout(() => setShowEngagementModal(true), 1500);
        }
      }
    } else {
      const eventName =
        followableType === FOLLOWABLE_TYPES.DOG
          ? ANALYTICS_EVENTS.PUPPY_REMOVED_FROM_FAVORITES
          : ANALYTICS_EVENTS.BREEDER_REMOVED_FROM_FAVORITES;

      sendRudderstackEvent(eventName, {
        buyer_profile_id: currentUserData.data.buyer_profile_id,
        view_feature: ANALYTICS_VIEW_FEATURES.FAVORITE_HEART_ICON,
        breeder_profile_id: analytics.breeder_profile_id,
        breeder_name: analytics.breeder_name,
        user_journey: ANALYTICS_USER_JOURNEYS.BUYER_BROWSING,
        ...(followableType === FOLLOWABLE_TYPES.DOG && {
          dog_id: followableTypeId
        }),
        ...analytics
      });

      try {
        await removeUserFollow();
      } finally {
        await userFollowsData.refetch();
        await summariesData.refetch();
        showPopover(FOLLOWABLE_STATUS_MESSAGE.REMOVED_FROM_FAVORITES);
        onChange?.({ status: FOLLOWABLE_STATUSES.REMOVED, followableTypeId });
      }
    }
  }

  function handleFavoriteClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();

    if (!isLoggedIn) {
      dispatch(
        pushParams(
          {
            direct_signup: SIGNUP_FORM_TYPES.SIGNUP,
            [SIGNUP_REF_ACTION]: SIGNUP_REF_ACTION_TYPES.FAVORITE_BUTTON
          },
          { hash: true }
        )
      );
    } else {
      updateFavorite();
    }
  }

  const popoverMessage =
    popoverStatus === FOLLOWABLE_STATUS_MESSAGE.REMOVED_FROM_FAVORITES ? (
      `${popoverStatus} favorites`
    ) : (
      <span>
        {popoverStatus} your{' '}
        <SmartLink to="/account/favorites" className=" underline">
          favorites
        </SmartLink>
      </span>
    );

  return (
    <>
      <UserFollowSuccessPopover
        title={popoverMessage}
        isVisible={showSuccessPopover}
        targetEl={targetEl}
        portalStyle={portalStyle || undefined}
      >
        <button
          type="button"
          className={classnames('flex items-center justify-center', className)}
          onClick={handleFavoriteClick}
          disabled={isLoading || isUpdating}
        >
          <Icon {...iconProps} />
          {iconProps.label && <div className="ml2">{iconProps.label}</div>}
        </button>
      </UserFollowSuccessPopover>
      <FavoriteEngagementMobileMenu
        analytics={{ view: analytics.view || '' }}
        isOpen={showEngagementModal}
        onCloseClick={() => setShowEngagementModal(false)}
        variant={
          followableType === FOLLOWABLE_TYPES.BREEDER_BREED
            ? 'breeder'
            : 'puppy'
        }
        breederFirstName={breederFirstName}
        breederProfilePhotoUrl={breederProfilePhotoUrl}
        puppyName={puppyName}
        puppyPhotoUrl={puppyPhotoUrl}
      />
    </>
  );
}
